/** A patient profile (the account holder or an attached dependent). */
export interface Patient {
  /** FHIR id — the universal patient identifier used by every MEDIS call. */
  fhirId: string;
  medisId: number | null;
  firstName: string;
  lastName: string;
  fullName: string;
  birthDay: Date | null;
  age: string;
  /** True for the account holder, false for attached dependents. */
  isPrimary: boolean;
  /** Emails of the accounts this patient is attached to (v2 populates it for the primary patient too). */
  attachedBy: string[];
  /** The patient's MEDIS activation code — lets another account attach them (v2 only). */
  actCode: string | null;
}

/**
 * Fresh values of the primary patient's volatile fields, from
 * `GET /v2/GetPatientRefresh` — re-read on every entry to the patients page
 * and merged into the cached `Patient` list (see `usePatientRefresh`).
 */
export interface PatientRefresh {
  /** The current one-time attach code, or null when the server sent none. */
  uniqueCode: string | null;
  /** Emails of the accounts the primary patient is attached to. */
  attachedBy: string[];
}

/** Raw Azure patient record (PascalCase, as returned by `/GetPatient`). */
export interface AzurePatientDto {
  MedisID?: number;
  FhirID?: string;
  FirstName?: string;
  LastName?: string;
  BirthDay?: string | null;
  Age?: string | number;
  Loaded?: boolean;
  AccessLists?: number;
  AttachedBy?: string[];
  ActCode?: string;
}

/** `Response` payload of `/GetPatient`. */
export interface GetPatientResponseDto {
  Patient?: AzurePatientDto | null;
  PatientsAttached?: AzurePatientDto[] | null;
  HavePatientsAttached?: boolean;
  PatientsAttachedCount?: number;
}

/** Outcome of a `/RegisterPatient` (attach) attempt. */
export type AddPatientOutcome =
  | 'registered'
  | 'attached'
  | 'invalidCode'
  | 'invalidSsn'
  | 'notDefined'
  | 'existingAccount'
  | 'alreadyAttached'
  | 'alreadyUsed'
  | 'attachedTooMany'
  | 'duplicateSsn'
  | 'unknown';

/** The success outcomes — anything else is surfaced as a form-level error. */
export const ADD_PATIENT_SUCCESS: ReadonlySet<AddPatientOutcome> = new Set([
  'registered',
  'attached',
]);

export interface AddPatientResult {
  success: boolean;
  outcome: AddPatientOutcome;
  /** Anonymized email of the existing account, when relevant. */
  username?: string;
  /** Register (not attach) only: accounts the patient was already attached to. */
  attachedAt?: string[];
}

/** Outcome of a `/v2/DetachPatient` attempt. */
export type DetachPatientOutcome =
  /** Association removed. */
  | 'detached'
  /** 400 — validation failed (e.g. the email is not in the patient's `AttachedBy`). */
  | 'invalid'
  /** 501 — the endpoint is not live on the server yet. */
  | 'notImplemented'
  | 'error';

/**
 * A detach request comes in one of two directions — only one identifier is
 * sent (see `DetachPatientRequestV2`):
 *   - `leaveAccount`    — remove yourself from the account with email `account`
 *                         (one of your patient's `attachedBy` emails).
 *   - `detachDependent` — remove a dependent patient (`patientFhirId`) that is
 *                         attached to your account.
 */
export type DetachPatientInput =
  | { mode: 'leaveAccount'; account: string }
  | { mode: 'detachDependent'; patientFhirId: string };

export interface DetachPatientResult {
  success: boolean;
  outcome: DetachPatientOutcome;
  /** Raw server message, when one is provided. */
  message?: string;
}

/** Input the form collects. `birthYear`/`ssnHash` are derived from the CNP. */
export interface AddPatientInput {
  cnp: string;
  activationCode: string;
  /**
   * True = attach a dependent to an account that already has patients;
   * false = first-time register (the account has no patient yet — GetPatient
   * returned 204). Defaults to true.
   */
  attach?: boolean;
}
